import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ProposalHeaderImage } from './proposalHeaderImage.models';
import {
  initState,
  proposalHeaderImageReducer,
  ProposalHeaderImageState,
} from './proposalHeaderImage.reducer';

interface ProposalHeaderImageContextProps {
  proposalHeaderImages: ProposalHeaderImage[];
  isLoading: boolean;
  isLoaded: boolean;
  fetchProposalHeaderImages: (organizationId: string) => Promise<void>;
}

const ProposalHeaderImageContext = createContext<
  ProposalHeaderImageContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProposalHeaderImageState>;
}>;

export const ProposalHeaderImageProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(proposalHeaderImageReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const proposalHeaderImages = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.entities, state.ids]
  );

  const fetchProposalHeaderImages: ProposalHeaderImageContextProps['fetchProposalHeaderImages'] =
    useCallback(
      async (organizationId) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }
        dispatch({ type: 'FETCH_PROPOSAL_HEADER_IMAGES' });
        try {
          const proposalHeaderImages = await httpClient.get<
            ProposalHeaderImage[]
          >(`/organizations/${organizationId}/proposal-header-images`);
          dispatch({
            type: 'FETCH_PROPOSAL_HEADER_IMAGES_SUCCESS',
            payload: { proposalHeaderImages },
          });
        } catch (error) {
          dispatch({
            type: 'FETCH_PROPOSAL_HEADER_IMAGES_FAILURE',
            payload: {
              error: `Failed to fetch proposal header images: ${error}`,
            },
          });
        }
      },
      [httpClient, isOnline]
    );

  return (
    <ProposalHeaderImageContext.Provider
      value={{
        proposalHeaderImages,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        fetchProposalHeaderImages,
      }}
    >
      {children}
    </ProposalHeaderImageContext.Provider>
  );
};

export const useProposalHeaderImage = () => {
  const context = useContext(ProposalHeaderImageContext);
  if (context === undefined) {
    throw new Error(
      'useProposalHeaderImage must be used within a ProposalHeaderImageProvider'
    );
  }
  return context;
};
